export function selectCampaign(entries, now) {
  return entries.reduce((winner, entry) => {
    if (!Number.isFinite(entry.start) || !Number.isFinite(entry.end) || entry.start >= entry.end || now < entry.start || now >= entry.end) return winner;
    const specificity = entry.specificity || 0;
    const winningSpecificity = winner?.specificity || 0;
    return !winner || specificity > winningSpecificity ||
      (specificity === winningSpecificity && (entry.priority > winner.priority ||
        (entry.priority === winner.priority && entry.id < winner.id))) ? entry : winner;
  }, null);
}

export function remainingTime(end, now) {
  const seconds = Math.max(0, Math.ceil((end - now) / 1000));
  return { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
}

if (typeof document !== 'undefined') {
  const start = () => {
    const config = document.getElementById('campaign-config');
    if (!config || window.ppCampaignController) return;
    let entries;
    try { entries = JSON.parse(config.textContent); } catch { return; }
    const abort = new AbortController();
    const { signal } = abort;
    const dismissed = new Map();
    let timer;
    let popupTimer;
    let pendingAutoOpen = false;
    let selectedId;
    let returnFocus;
    const dialog = document.getElementById('campaign-dialog');
    const teaser = document.getElementById('campaign-teaser');
    const popupHost = document.getElementById('campaign-popup-content');
    const delay = Number(config.dataset.popupDelay) * 1000;
    const cooldown = Number(config.dataset.popupCooldown) * 3600000;
    const key = entry => `pp-campaign-dismissed:${config.dataset.country || ""}:${entry.id}:${entry.start}`;
    const wasDismissed = entry => {
      let until = dismissed.get(key(entry)) || 0;
      try { until = Math.max(until, Number(localStorage.getItem(key(entry))) || 0); } catch {}
      return until > Date.now();
    };
    const close = (remember = true) => {
      if (remember) { clearTimeout(popupTimer); pendingAutoOpen = false; }
      const entry = entries.find(item => item.id === selectedId);
      if (remember && entry) {
        const until = Date.now() + cooldown;
        dismissed.set(key(entry), until);
        try { localStorage.setItem(key(entry), String(until)); } catch {}
      }
      const wasOpen = dialog?.open;
      const focusTarget = returnFocus;
      returnFocus = undefined;
      dialog?.close();
      if (wasOpen && focusTarget?.isConnected && !focusTarget.closest('[hidden]')) focusTarget.focus();
    };
    const open = (automatic = false) => {
      const entry = selectCampaign(entries, Date.now());
      if (!entry || entry.id !== selectedId || !popupHost?.firstElementChild || (automatic && wasDismissed(entry))) return;
      if (document.hidden) { pendingAutoOpen = automatic; return; }
      const blocked = [...document.querySelectorAll('dialog[open], [aria-modal="true"], #shopify-pc__banner')]
        .some(element => element !== dialog && !element.closest('[hidden], [aria-hidden="true"]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      if (dialog.open || blocked) {
        if (automatic && blocked) popupTimer = setTimeout(() => open(true), 1000);
        return;
      }
      const picture = popupHost.querySelector('template[data-popup-image]');
      if (picture) picture.replaceWith(picture.content.cloneNode(true));
      returnFocus = document.activeElement;
      pendingAutoOpen = false;
      clearTimeout(popupTimer);
      dialog.showModal();
    };
    const updateSlots = entry => {
      document.querySelectorAll('[data-campaign-slot]').forEach(slot => {
        const template = [...slot.querySelectorAll(':scope > template[data-campaign-id]')].find(item => item.dataset.campaignId === entry?.id);
        const host = slot.querySelector(':scope > [data-campaign-content]');
        let fallback = slot.querySelector(':scope > [data-campaign-fallback]');
        const nextId = template ? entry.id : '';
        if (host.dataset.campaignId !== nextId) {
          host.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
          host.dataset.campaignId = nextId;
        }
        host.hidden = !template;
        if (!template && fallback?.tagName === 'TEMPLATE') {
          const div = document.createElement('div');
          div.dataset.campaignFallback = '';
          div.append(fallback.content.cloneNode(true));
          fallback.replaceWith(div);
          fallback = div;
        }
        if (fallback) fallback.hidden = Boolean(template);
        slot.hidden = !template && !fallback;
      });
    };
    const tick = () => {
      clearTimeout(timer);
      const now = Date.now();
      const entry = selectCampaign(entries, now);
      updateSlots(entry);
      if (entry?.id !== selectedId) {
        clearTimeout(popupTimer);
        pendingAutoOpen = false;
        close(false);
        selectedId = entry?.id;
        popupHost?.replaceChildren();
        const template = [...document.querySelectorAll('template[data-campaign-popup]')].find(item => item.dataset.campaignPopup === selectedId);
        if (teaser) teaser.hidden = !template;
        if (template) {
          popupHost.append(template.content.cloneNode(true));
          teaser.textContent = template.dataset.teaser;
          if (!wasDismissed(entry) && config.dataset.designMode !== 'true') popupTimer = setTimeout(() => open(true), delay);
        }
      }
      if (pendingAutoOpen && !document.hidden) {
        pendingAutoOpen = false;
        popupTimer = setTimeout(() => open(true), 0);
      }
      if (entry) {
        const remaining = remainingTime(entry.end, now);
        document.querySelectorAll('[data-campaign-clock]').forEach(clock => {
          clock.hidden = false;
          for (const [unit, value] of Object.entries(remaining)) {
            const element = clock.querySelector(`[data-${unit}]`);
            if (element) element.textContent = String(value).padStart(2, '0');
          }
        });
      }
      if (!document.hidden) {
        const boundaries = entries.flatMap(item => [item.start, item.end]).filter(time => time > now);
        const hasClock = Boolean(document.querySelector('[data-campaign-content]:not([hidden]) [data-campaign-clock]'));
        const wait = hasClock ? 1000 - now % 1000 : Math.min(60000, ...boundaries.map(time => time - now));
        if (hasClock || boundaries.length) timer = setTimeout(tick, Math.max(20, wait));
      }
    };
    teaser?.addEventListener('click', () => open(), { signal });
    dialog?.addEventListener('cancel', event => { event.preventDefault(); close(); }, { signal });
    dialog?.addEventListener('click', event => {
      if (event.target.closest('[data-campaign-close]') || event.target === dialog) close();
    }, { signal });
    document.addEventListener('visibilitychange', tick, { signal });
    document.addEventListener('shopify:section:load', tick, { signal });
    window.addEventListener('pageshow', tick, { signal });
    window.ppCampaignController = { refresh: tick, destroy: () => { abort.abort(); clearTimeout(timer); clearTimeout(popupTimer); close(false); delete window.ppCampaignController; } };
    tick();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
